import React, { FormEvent } from 'react';

import styled from 'styled-components';

import { useTranslation } from 'react-i18next';

import UIButton, { EUIButtonVariant } from '@aquariuxfintech/aqx-trader-react-ui/button';
import UIFormItem, { EUIFormItemVariant } from '@aquariuxfintech/aqx-trader-react-ui/form';
import { EFontSize, ESpacing, ETypography } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import UIInput, { EUIInputVariant } from '@aquariuxfintech/aqx-trader-react-ui/input';
import UIText from '@aquariuxfintech/aqx-trader-react-ui/text';

import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { EInputType } from '@aqx-core/common/hooks/form/use-otp-login-state';

import AuthResendText from '../../../shared/resend-text';

interface IOtpLoginStep2OtpProps {
  inputType: EInputType;
  authLoginLoading: boolean | undefined;
  otpError: string;
  isOtpBtnDisabled: boolean;
  onOtpChange: (pin: string) => void;
  onOtpValidate: () => boolean;
  onUsePassword: () => void;
  onBack: () => void;
  onFormSubmit: (e?: FormEvent<HTMLFormElement>) => void;
  onResendOtp: () => void;
  remainingTimeText: string;
  isResendDisabled: boolean;
}

const OtpLoginStep2Otp: FCC<IOtpLoginStep2OtpProps> = ({
  inputType,
  authLoginLoading,
  otpError,
  isOtpBtnDisabled,
  onOtpChange,
  onOtpValidate,
  onBack,
  onFormSubmit,
  onResendOtp,
  remainingTimeText,
  isResendDisabled
}) => {
  const { t } = useTranslation();

  const handleFormSubmit = (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!isOtpBtnDisabled && onOtpValidate()) {
      onFormSubmit();
    }
  };

  return (
    <StyledContainer onSubmit={handleFormSubmit}>
      <StyledTitleContainer>
        <UIText typography={ETypography.DISPLAY_M_BOLD}>{t('Enter Code')}</UIText>
        <StyledDescription typography={ETypography.TEXT_XL_REGULAR}>
          {inputType === EInputType.PHONE
            ? t('Please enter the 6-digits code we sent to your phone number.')
            : t('Please enter the 6-digits code we sent to your email.')}
        </StyledDescription>
      </StyledTitleContainer>

      <StyledFormInputsContainer>
        <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} size="large">
          <UIInput
            variant={EUIInputVariant.BORDERED_PIN}
            onChange={onOtpChange}
            error={otpError}
            loading={authLoginLoading}
            onEnterKey={() => {
              handleFormSubmit();
            }}
          />
        </UIFormItem>

        <AuthResendText
          descriptionText={t("Didn't receive it?")}
          actionText={t('Resend Code')}
          $marginTop={ESpacing.XL}
          onClickResend={onResendOtp}
          isResendDisabled={isResendDisabled}
          remainingTimeText={remainingTimeText}
        />
      </StyledFormInputsContainer>

      <StyledButtonContainer>
        <UIButton
          variant={EUIButtonVariant.FILLED_THEMED}
          loading={authLoginLoading}
          dataTestId={EDataTestId.LOGIN_SUBMIT}
          size="large"
          disabled={isOtpBtnDisabled}
          htmlType="submit"
        >
          {t('Log In')}
        </UIButton>

        <UIButton
          variant={EUIButtonVariant.TEXT_ONLY_THEMED}
          loading={authLoginLoading}
          dataTestId={EDataTestId.LOGIN_SUBMIT}
          onClick={onBack}
        >
          {t('Back to previous')}
        </UIButton>
      </StyledButtonContainer>
    </StyledContainer>
  );
};

export default OtpLoginStep2Otp;

const StyledContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.XXXL}px;
  width: 512px;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.REGULAR}px;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.M}px;

  button:nth-child(2) {
    font-size: ${EFontSize.TEXT_L}px !important;
  }
`;

const StyledFormInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.XL}px;
`;

const StyledDescription = styled(UIText)`
  color: ${({ theme }) => theme.themeColors.text.subtle};
  margin-top: ${ESpacing.XS}px;
`;
